import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { useNavigate } from 'react-router-dom';
import { ManagerDashboardBid, mapStatusToDisplay } from '../types';
import styles from './BidCard.module.scss';

interface BidCardProps {
  bid: ManagerDashboardBid;
  onViewNotes: (bid: ManagerDashboardBid) => void;
  viewOnly?: boolean;
}

const BidCard: React.FC<BidCardProps> = ({ bid, onViewNotes, viewOnly = false }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}-${day}-${year}`;
    } catch (_error) {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return formatDate(dateString);
    } catch (_error) {
      return dateString;
    }
  };

  const getStatusClass = (status: string): string => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'in review' || lowerStatus === 'pending review') {
      return styles.statusPendingReview;
    }
    if (lowerStatus === 'approved') return styles.statusApproved;
    if (lowerStatus === 'needs modification') return styles.statusNeedsModification;
    return styles.statusDefault;
  };

  const latestNote = bid.notes && bid.notes.length > 0 ? bid.notes[0] : null;

  const handleReviewBid = () => {
    navigate(`/bdportal/bid-analysis/template/${bid.bid_id}`);
  };

  return (
    <div className={styles.bidCard}>
      {/* Card Header with colored top border */}
      <div className={styles.cardHeader}>
        <div className={styles.headerContent}>
          <div>
            <h3 className={styles.moleculeName} title={bid.molecule_name}>
              {bid.molecule_name}
            </h3>
            <div className={styles.bidId} title={`Bid ID: ${bid.bid_id}`}>
              Bid ID: {bid.bid_id}
            </div>
          </div>
          <span className={`${styles.statusBadge} ${getStatusClass(bid.status)}`}>
            <i className="pi pi-circle-fill" style={{ fontSize: '0.5rem', marginRight: '0.375rem' }}></i>
            {mapStatusToDisplay(bid.status).toUpperCase()}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className={styles.cardBody}>
        {/* Metadata Row 1 */}
        <div className={styles.metadataRow}>
          <div className={styles.metadataItem}>
            <i className={`pi pi-user ${styles.metadataIcon}`}></i>
            <span className={styles.metadataLabel}>{bid.analyst_name}</span>
          </div>
          <div className={styles.metadataItem}>
            <i className={`pi pi-calendar ${styles.metadataIcon}`}></i>
            <span className={styles.metadataLabel}>{formatDate(bid.target_launch_date)}</span>
          </div>
        </div>

        {/* Metadata Row 2 */}
        <div className={styles.metadataRow}>
          <div className={styles.metadataItem}>
            <i className={`pi pi-tag ${styles.metadataIcon}`}></i>
            <span className={styles.metadataLabel}>{bid.template_name}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressSection}>
          <div className={styles.progressInfo}>
            <i className="pi pi-info-circle" style={{ fontSize: '0.875rem', color: '#3b82f6' }}></i>
            <span className={styles.progressText}>
              Analysis {bid.progress}% complete
            </span>
          </div>
          <ProgressBar 
            value={bid.progress} 
            showValue={false} 
            className={styles.progressBar}
            style={{ height: '0.375rem' }}
          />
        </div>

        {/* Analyst Notes */}
        <div className={styles.notesSection}>
          <div className={styles.notesSectionHeader}>
            <i className="pi pi-comment" style={{ fontSize: '0.875rem', marginRight: '0.375rem' }}></i>
            <span className={styles.notesTitle}>Analyst Notes</span>
            {bid.notes.length > 0 && (
              <button
                onClick={() => onViewNotes(bid)}
                className={styles.viewAllButton}
              >
                <i className="pi pi-external-link" style={{ fontSize: '0.75rem', marginRight: '0.25rem' }}></i>
                View All
              </button>
            )}
          </div>
          
          {latestNote ? (
            <div className={styles.noteItem}>
              <div className={styles.noteAuthor}>
                <span className={styles.authorName}>{latestNote.created_by}</span>
                <span className={styles.noteTime}>{formatTimeAgo(latestNote.created_at)}</span>
              </div>
              <div className={styles.noteContent}>
                {latestNote.content.length > 120
                  ? `${latestNote.content.substring(0, 120)}...`
                  : latestNote.content}
              </div>
            </div>
          ) : (
            <div className={styles.noNotes}>
              <span>No analyst notes available</span>
            </div>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className={styles.cardFooter}>
        <Button
          label={viewOnly ? "View Bid" : "Review Bid"}
          icon="pi pi-eye"
          onClick={handleReviewBid}
          className={styles.reviewButton}
        />
      </div>
    </div>
  );
};

export default BidCard;
